import { useEffect } from 'react'
import { legacyUiApi } from '../api/legacyUi'
import type { PlatformKind } from './platform/platform'

/**
 * Счётчик открытий прежнего интерфейса (этап 5 шаг 1): по нему видно, когда
 * прежний интерфейс можно убрать из мини-приложений и PWA. Без ПДн: только
 * вид события, среда и экраны нового интерфейса без id («/groups/:id»).
 * Сбой отправки ничего не ломает: счётчик - не повод держать пользователя.
 */

/** Где нажали «Открыть прежнюю версию» и откуда пришли на этот экран. */
export interface LegacyWhere {
  screen: string
  fromScreen: string
}

/**
 * Слова маршрутов нового интерфейса (RedesignApp). Всё остальное в адресе -
 * id, ключ занятия, код тарифа - уходит как «:id»: так в счётчик не попадёт
 * ни id, ни текст, даже если маршрут когда-нибудь понесёт его в пути.
 */
const ROUTE_WORDS = new Set([
  'today', 'setup', 'schedule', 'sessions', 'events', 'new', 'edit', 'add', 'sport', 'athletes', 'groups',
  'club', 'directions', 'switch', 'channels', 'team', 'subscription', 'plans', 'change', 'payment', 'return',
  'payments', 'hub', 'actions', 'bot',
  // этап 5 шаг 4: направления, справочник тренеров, мелочи клуба
  'coaches', 'rename', 'join', 'delete', 'feedback', 'about',
])

/** Маршрут без id и имён: «/groups/0192…/edit» -> «/groups/:id/edit». Пусто - неизвестно. */
export function screenOf(pathname: string | undefined): string {
  if (!pathname) return ''
  const parts = pathname
    .split('?')[0]
    .split('/')
    .filter(Boolean)
    .slice(0, 6)
    .map((p) => (ROUTE_WORDS.has(p) ? p : ':id'))
  return parts.length > 0 ? `/${parts.join('/')}` : ''
}

/** Сколько ждать ответа перед перезапуском в прежний интерфейс. */
const SWITCH_WAIT_MS = 1500

/** «Открыть прежнюю версию»: событие уходит до перезапуска, но не дольше SWITCH_WAIT_MS. Не бросает. */
export async function reportLegacySwitch(platform: PlatformKind, where: LegacyWhere): Promise<void> {
  const send = legacyUiApi
    .record({
      event: 'switch',
      platform,
      screen: where.screen || undefined,
      from_screen: where.fromScreen || undefined,
    })
    .catch(() => undefined)
  await Promise.race([send, new Promise<void>((resolve) => setTimeout(resolve, SWITCH_WAIT_MS))])
}

// Одно событие на запуск страницы: повторный рендер и StrictMode не дублируют.
let launchReported = false

/** Запуск прежнего интерфейса: одно событие, когда вход уже есть (enabled). */
export function useLegacyLaunchReport(enabled: boolean, platform: PlatformKind) {
  useEffect(() => {
    if (!enabled || launchReported) return
    launchReported = true
    void legacyUiApi.record({ event: 'launch', platform }).catch(() => undefined)
  }, [enabled, platform])
}

/** Только для тестов: снова разрешить событие запуска. */
export function resetLegacyLaunchReportForTests() {
  launchReported = false
}
